/**
 * @fileoverview Progress - Progress bar component
 *
 * Purpose: Display progress or loading states
 * Dependencies: Radix UI Progress primitive
 * Used by: File uploads, multi-step forms, loading indicators
 *
 * Features:
 * - Animated progress bar
 * - Accessible (ARIA attributes)
 * - Percentage value support
 * - Smooth transitions
 * - Customizable colors
 *
 * @example
 * <Progress value={33} />
 *
 * @example
 * // With label
 * <div className="space-y-2">
 *   <div className="flex justify-between text-sm">
 *     <span>Uploading...</span>
 *     <span>60%</span>
 *   </div>
 *   <Progress value={60} />
 * </div>
 */

import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";

import { cn } from "@/utils/misc";

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>
>(({ className, value, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(
      "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
      className,
    )}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className="h-full w-full flex-1 bg-primary transition-all"
      style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
    />
  </ProgressPrimitive.Root>
));
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
